/* eslint-disable no-param-reassign */
type ObjectType = Record<string, any>;

type OptionsType = {
  mergeArrays: boolean;
};

type UnionToIntersectionType<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

/**
 * Проверка на принадлежность объекту
 * @param {any} object
 */
const isObject = (obj: any) => {
  if (typeof obj === 'object' && obj !== null) {
    if (typeof Object.getPrototypeOf === 'function') {
      const prototype = Object.getPrototypeOf(obj);
      return prototype === Object.prototype || prototype === null;
    }

    return Object.prototype.toString.call(obj) === '[object Object]';
  }

  return false;
};

/**
 * Глубокое слияние нескольких объектов
 * @param {Array<object>} objects объекты для слияния
 */
const mergeDeep = <T extends ObjectType[]>(
  ...objects: T
): UnionToIntersectionType<T[number]> =>
  objects.reduce((result, current) => {
    Object.keys(current).forEach(key => {
      if (Array.isArray(result[key]) && Array.isArray(current[key])) {
        result[key] = mergeDeep.options.mergeArrays
          ? Array.from(new Set((result[key] as unknown[]).concat(current[key])))
          : current[key];
      } else if (isObject(result[key]) && isObject(current[key])) {
        result[key] = mergeDeep(result[key] as ObjectType, current[key] as ObjectType);
      } else {
        result[key] = current[key];
      }
    });

    return result;
  }, {}) as any;

const defaultOptions: OptionsType = {
  mergeArrays: false,
};

mergeDeep.options = defaultOptions;

mergeDeep.withOptions = <T extends ObjectType[]>(
  options: Partial<OptionsType>,
  ...objects: T
) => {
  mergeDeep.options = {
    ...defaultOptions,
    ...options,
  };

  const result = mergeDeep(...objects);

  mergeDeep.options = defaultOptions;

  return result;
};

export default mergeDeep;
